import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { OverlayBaseController } from '@ionic/angular/util/overlay';
import { Observable, throwError } from 'rxjs';
import { catchError, flatMap, map } from 'rxjs/operators';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { Paginated, Phase, Program, Question } from '@haleo-frontend/data-access/models';
import { AppService, EnvironmentUtils } from '@haleo-frontend/utils';

import { ConsentFormService } from './consent-form.service';

@Injectable({
    providedIn: 'root'
})
export class ProgramService {

    constructor(private http: HttpClient,
                private consentFormService: ConsentFormService,
                private router: Router,
                private appService: AppService) {
    }

    getPrograms(withSubResources = false): Observable<Program[]> {

        // Only HALEO programs need hidden phase data
        const embed = '?embed=phases,modules,contents' + (this.appService.getAppKey() === 'haleo' ? ',hidden' : '');

        return this.http.get<Paginated>(EnvironmentUtils.env.api + 'programs' + (withSubResources ? embed : ''))
            .pipe(map(data => {

                switch (this.appService.getAppKey()) {
                    case 'haleo':
                        return data.data.filter(program => program.key === EnvironmentUtils.env.programKeys.cbtiProgram ||
                            program.key === EnvironmentUtils.env.programKeys.sleepCoachingProgram ||
                            program.key === EnvironmentUtils.env.programKeys.sleepCoachingNewParentProgram ||
                            program.key === EnvironmentUtils.env.programKeys.cbtiShiftWorkProgram ||
                            program.key === EnvironmentUtils.env.programKeys.cbtiNightmareProgram ||
                            program.key === EnvironmentUtils.env.programKeys.cbtiShiftWorkNightmareProgram ||
                            program.key === EnvironmentUtils.env.programKeys.cbtiNewParentProgram);
                    case 'sleep-country':
                    case 'dormez-vous':
                    case 'sleep-assistant':
                        return data.data.filter(program => program.key === EnvironmentUtils.env.programKeys.selfGuidedCbtiProgram);
                }

                return data.data;
            }));
    }

    getProgramVisibleQuestions(program: Program): Observable<Question[]> {
        return this.http.get<Paginated>(EnvironmentUtils.env.api + `programs/${program.uuid}/questions?appKey=${this.appService.getAppKey()}&page=1&limit=1000&status=active,mandatory`)
            .pipe(map(data => data.data));
    }

    getHomeProgram(uuid: string): Observable<Phase[]> {
        return this.http.get<Paginated>(EnvironmentUtils.env.api + 'programs/' + uuid + '/phases?key=sleep-country&embed=modules,contents')
            .pipe(map(data => data.data));
    }

    getProgramInactiveQuestions(program: Program): Observable<Question[]> {
        return this.http.get<Paginated>(EnvironmentUtils.env.api + `programs/${program.uuid}/questions?page=1&limit=1000&status=inactive`)
            .pipe(map(data => data.data));
    }

    getProgramQuestions(program: Program): Observable<Question[]> {
        return this.http.get<Paginated>(EnvironmentUtils.env.api + `programs/${program.uuid}/questions?appKey=${this.appService.getAppKey()}&page=1&limit=1000`)
            .pipe(map(data => data.data));
    }

    getSleepCountryPhase(uuid: string, phase: string): Observable<Phase[]> {
        return this.http.get<Paginated>(EnvironmentUtils.env.api + 'programs/' + uuid + `/phases?key=${phase}&embed=modules,contents`)
            .pipe(map(data => data.data));
    }

    signUpAndRedirect(programKey: string, coupon: string, loader: OverlayBaseController<any, any>): Observable<any> {

        return this.http.post<Program>(EnvironmentUtils.env.api + 'programs/sign-up', {programKey, coupon})
            .pipe(flatMap(() => this.consentFormService.getConsentFormsToAccept()),
                flatMap(consentForms => {

                    loader.dismiss();

                    if (consentForms.length > 0) {
                        return fromPromise(this.router.navigate(['/consent-form', consentForms[0].uuid]));
                    } else {
                        return fromPromise(this.router.navigate(['/tabs']));
                    }
                }),
                catchError(exception => {
                    loader.dismiss();
                    return throwError(exception);
                }));
    }
}
